import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { documentAPI, TripDocument } from '../../api/documents';
import { DocumentCard } from './DocumentCard';
import { DocumentPreviewModal } from './DocumentPreviewModal';
import toast from 'react-hot-toast';

interface DocumentListProps {
  entityType: 'trip' | 'expense' | 'payment';
  entityId: number;
  canDelete?: boolean;
  title?: string;
  refreshTrigger?: number; // Trigger refresh when this changes
}

export const DocumentList: React.FC<DocumentListProps> = ({
  entityType,
  entityId,
  canDelete = false,
  title,
  refreshTrigger,
}) => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<TripDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewDoc, setPreviewDoc] = useState<TripDocument | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      let docs: TripDocument[] = [];
      
      switch (entityType) {
        case 'trip':
          docs = await documentAPI.getByTrip(entityId);
          break;
        case 'expense':
          docs = await documentAPI.getByExpense(entityId);
          break;
        case 'payment':
          docs = await documentAPI.getByPayment(entityId);
          break;
      }
      
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (entityId) {
      fetchDocuments();
    }
  }, [entityId, fetchDocuments, refreshTrigger]);

  const handlePreview = async (doc: TripDocument) => {
    if (doc.mimeType === 'application/pdf') {
      // For PDF, download and open in new tab
      try {
        const blob = await documentAPI.preview(doc.id);
        const url = window.URL.createObjectURL(blob);
        const newWindow = window.open(url, '_blank');
        if (!newWindow) {
          toast.error(t('documents.cannotPreview'));
          window.URL.revokeObjectURL(url);
        } else {
          // Clean up after window closes or after a delay
          setTimeout(() => window.URL.revokeObjectURL(url), 60000); // 60 seconds
        }
      } catch (error: any) {
        toast.error(error.response?.data?.message || t('documents.downloadError'));
      }
    } else if (doc.mimeType.startsWith('image/')) {
      // Show image in modal
      setPreviewDoc(doc);
    } else {
      // For other file types, show error
      toast.error(t('documents.cannotPreview'));
    }
  };

  const handleDownload = async (doc: TripDocument) => {
    try {
      const blob = await documentAPI.download(doc.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t('documents.downloadError'));
    }
  };

  const handleDelete = async (docId: number) => {
    if (!window.confirm(t('documents.confirmDelete'))) return;

    setDeletingId(docId);
    try {
      await documentAPI.delete(docId);
      setDocuments(documents.filter((d) => d.id !== docId));
      toast.success(t('documents.deleteSuccess'));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('documents.deleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4 text-gray-500">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {title && (
        <div className="flex items-center space-x-2">
          <FileText className="w-5 h-5 text-gray-500" />
          <h3 className="text-sm font-medium text-gray-700">{title}</h3>
        </div>
      )}

      {documents.length === 0 ? (
        <p className="text-sm text-gray-500 py-2">{t('documents.noDocuments')}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {documents.map((doc) => (
            <DocumentCard
              key={doc.id}
              document={doc}
              onPreview={() => handlePreview(doc)}
              onDownload={() => handleDownload(doc)}
              onDelete={() => handleDelete(doc.id)}
              canDelete={canDelete}
              isDeleting={deletingId === doc.id}
            />
          ))}
        </div>
      )}

      {previewDoc && (
        <DocumentPreviewModal
          document={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
};
